import { useMutation } from '@apollo/client';
import { Box, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoTooltip from 'DesignSystem/DataDisplay/InfoTooltip/InfoTooltip';
import { TContentBlockType } from 'FeatureModules/ProductPage/types/product.types';
import { useFormikContext } from 'formik';
import { UPDATE_CONTENT_BLOCK_ENABLED } from 'graphql/secure/mutations';
import useMessages from 'hooks/useMessages';
import React, { FC, useState } from 'react';
import ImportContentBlock from './ImportContentBlock';

interface IHeaderProps {
  title: string;
  showEnabled?: boolean;
  helperText?: string;
  contentBlockId: string;
  contentType?: TContentBlockType;
  setContent?: (data: unknown) => void;
}

const Enabled = styled(Typography)(() => ({
  fontSize: 14,
}));

interface ISwitchInputProps extends React.HTMLAttributes<HTMLDivElement> {
  'data-testId'?: string;
}

const Header: FC<IHeaderProps> = ({
  title,
  showEnabled,
  helperText,
  contentBlockId,
  contentType,
  setContent,
}) => {
  const { values } = useFormikContext();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  // @ts-ignore
  const [enabled, setEnabled] = useState<boolean>(values?.enabled);
  const [updateContentBlockEnabled, { loading }] = useMutation(UPDATE_CONTENT_BLOCK_ENABLED, {
    // The cache is not properly updated cause we don't get back the full version content blocks list
    refetchQueries: ['GetLatestVersion'],
    onError: () => {
      setErrorMessage(
        `There was an error ${enabled ? 'enabling' : 'disabling'} your content block`
      );
      setEnabled(!enabled);
    },
    onCompleted: () => {
      setSuccessMessage(`Successfully  ${enabled ? 'enabled' : 'disabled'} content block`);
    },
  });

  const handleUpdateContentBlockEnabled = async () => {
    const contentBlockEnabled = !enabled;
    setEnabled(contentBlockEnabled);
    await updateContentBlockEnabled({
      variables: {
        id: contentBlockId,
        enabled: contentBlockEnabled,
      },
    });
  };

  return (
    <Box display="flex" alignItems="start" justifyContent="space-between">
      <Box display="flex" alignItems="center">
        <Box display="flex" alignItems="center">
          <Typography variant="h5">{title}</Typography>
          {helperText && <InfoTooltip text={helperText} />}
        </Box>
        {showEnabled && (
          <Box display="flex" alignItems="center" pt="4px">
            <Switch
              checked={enabled}
              disabled={loading}
              onChange={handleUpdateContentBlockEnabled}
              name="checked"
              inputProps={
                {
                  'data-testId': 'field-switch-enabled',
                } as ISwitchInputProps
              }
            />
            <Enabled variant="h6">{enabled ? 'Enabled' : 'Disabled'}</Enabled>
          </Box>
        )}
      </Box>
      {contentType && setContent && (
        <ImportContentBlock
          contentType={contentType}
          // @ts-ignore
          setContent={newValues => setContent({ enabled, id: values?.id, ...newValues })}
        />
      )}
    </Box>
  );
};

export default Header;
